import { HttpErrorResponse } from '@angular/common/http';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, Observable, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AsyncJobType } from '@recruitee/account/data';
import {
  CorrelationId,
  EntityCollectionAdapter,
  EntityState,
  GenericEntity,
  GenericMetaOptions,
  pipeState,
} from '@recruitee/ngrx';

import { AsyncJobActions } from './store/async-job.store';

type BulkActionReducer<Name extends string, Params, Entity extends GenericEntity> = BulkAction<
  Name,
  Params,
  Entity
> &
  BulkActionSuccess<Name, Entity> &
  BulkActionFail<Name, Entity>;
type BulkAction<Name extends string, Params, Entity extends GenericEntity> = Record<
  Name,
  (
    state: EntityState<Entity>,
    payload: Required<CorrelationId> & { params: Params },
  ) => EntityState<Entity>
>;
type BulkActionSuccess<Name extends string, Entity extends GenericEntity> = Record<
  `${Name}Success`,
  (
    state: EntityState<Entity>,
    payload: Required<CorrelationId> & { data: { uuid: string } },
  ) => EntityState<Entity>
>;
type BulkActionFail<Name extends string, Entity extends GenericEntity> = Record<
  `${Name}Failed`,
  (
    state: EntityState<Entity>,
    payload: Required<CorrelationId> & { error: HttpErrorResponse },
  ) => EntityState<Entity>
>;
type BulkActionSet<T extends string> = Record<
  T,
  { type: T } & ((arg: { payload: Required<CorrelationId> & { params: any } }) => {
    type: T;
    payload: Required<CorrelationId> & { params: any };
  })
> &
  Record<
    `${T}Success`,
    { type: `${T}Success` } & ((arg: {
      payload: Required<CorrelationId> & { data: { uuid: string } };
    }) => { type: T; payload: Required<CorrelationId> & { data: { uuid: string } } })
  > &
  Record<
    `${T}Failed`,
    { type: `${T}Failed` } & ((arg: {
      payload: Required<CorrelationId> & { error: HttpErrorResponse };
    }) => {
      type: T;
      payload: Required<CorrelationId> & { error: HttpErrorResponse };
    })
  >;

export const bulkActionFactory =
  <Entity extends { uuid: string }>(adapter: EntityCollectionAdapter<Entity, GenericMetaOptions>) =>
  <T extends string, S extends AsyncJobType>(name: T, scope: S) =>
  <Params>() =>
    <BulkActionReducer<T, Params, Entity>>{
      [name]: (state: EntityState<Entity>, payload: Required<CorrelationId> & { params: Params }) =>
        adapter.markAsPending(state, {
          entities: [payload.correlationId],
          scope,
        }),
      [`${name}Success`]: (
        state: EntityState<Entity>,
        payload: Required<CorrelationId> & { data: Entity },
      ) =>
        pipeState(
          state,
          next => adapter.setAlias(next, { [payload.correlationId]: payload.data.uuid }),
          next =>
            adapter.markAsLoaded(next, {
              entities: [payload.data.uuid],
              scope,
            }),
          next => adapter.addOne(next, payload.data, { scope }),
        ),
      [`${name}Failed`]: (
        state: EntityState<Entity>,
        payload: Required<CorrelationId> & { error: HttpErrorResponse },
      ) =>
        adapter.setError(state, payload.error, {
          entities: [payload.correlationId],
          scope,
        }),
    };

export const bulkActionEffectFactory = <
  BulkActions extends BulkActionSet<string>,
  ActionName extends Extract<keyof BulkActions, string>,
  PickedActions extends Pick<
    BulkActions,
    ActionName | `${ActionName}Success` | `${ActionName}Failed`
  >,
>(
  actionsStream$: Actions,
  actions: PickedActions,
  name: ActionName,
  type: AsyncJobType,
  handler: (
    params: ReturnType<PickedActions[ActionName]>['payload']['params'],
  ) => Observable<Parameters<BulkActions[`${ActionName}Success`]>[0]['payload']['data']>,
) =>
  createEffect(() =>
    actionsStream$.pipe(
      ofType(actions[name]),
      mergeMap(({ payload }) =>
        handler(payload.params).pipe(
          switchMap(({ uuid }) => [
            AsyncJobActions.create({
              payload: { job: { uuid, type } },
            }),
            actions[`${name}Success`]({
              payload: { correlationId: payload.correlationId, data: { uuid } },
            }),
          ]),
          catchError(error =>
            of(
              actions[`${name}Failed`]({
                payload: { correlationId: payload.correlationId, error },
              }),
            ),
          ),
        ),
      ),
    ),
  );
